import { ApiService, type EvmApi } from '@/service/api';
import type { AxiosInstance } from 'axios';
import { EvmEventEmitter, type EvmEventEmitterInstance } from '@/service/utils/eventEmitter';
import { type CacheServiceConfig, EvmCacheService, type EvmCacheServiceInstance } from '@/service/utils/cache';
import { Config, type ConfigProviderInstance } from '@/service/config';
import { EvmOffer, type EvmOfferInterface } from '@/service/EVM/offer';
import TokenWrapperService, { type TokenWrapperFactoryInterface } from '@/service/EVM/tokenWrapper';
import { EvmNFTBuyer, type EvmNFTBuyerInterface } from '@/service/EVM/NFTBuyer';
import { EvmListingFactory, type EvmListingInterface } from '@/service/EVM/listing';
import { OrderValidator, type OrderValidatorInterface } from '~/service/utils/validator/order/index.';

export interface CoreConfig {
    axios: AxiosInstance;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    cash?: CacheServiceConfig<string, any>;
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const createDefaultCacheConfig = (): CacheServiceConfig<string, any> => ({
    cache: new Map(),
    timers: []
});

export class CoreServices {
    public readonly axios: AxiosInstance;
    public readonly api: EvmApi;
    public readonly eventEmitter: EvmEventEmitterInstance;
    public readonly cache: EvmCacheServiceInstance;
    public readonly providerConfig: ConfigProviderInstance;

    constructor(config: CoreConfig) {
        this.axios = config.axios;
        this.providerConfig = new Config();
        this.api = new ApiService(this.axios);
        this.eventEmitter = new EvmEventEmitter();
        this.cache = new EvmCacheService(config.cash ? config.cash : createDefaultCacheConfig());
    }

    public createEvmOffer(params: Omit<EvmOfferInterface, 'eventEmitter'>): EvmOffer {
        return new EvmOffer({
            ...params,
            eventEmitter: this.eventEmitter
        });
    }

    public createTokenWrapper(config: Omit<TokenWrapperFactoryInterface, 'eventEmitter'>): TokenWrapperService {
        return new TokenWrapperService({
            ...config,
            eventEmitter: this.eventEmitter
        });
    }

    public createEvmNFTBuyer(config: Omit<EvmNFTBuyerInterface, 'eventEmitter'>): EvmNFTBuyer {
        return new EvmNFTBuyer({
            ...config,
            eventEmitter: this.eventEmitter
        });
    }

    public createEvmListing(params: Omit<EvmListingInterface, 'eventEmitter' | 'chainMapById' | 'approveAbi'>): EvmListingFactory {
        const { abi } = this.providerConfig.configs.evm;

        return new EvmListingFactory({
            ...params,
            approveAbi: abi.type,
            eventEmitter: this.eventEmitter
        });
    }

    public createEvmOrderValidator(config: Omit<OrderValidatorInterface, 'eventEmitter'>): OrderValidator {
        return new OrderValidator
        ({
            ...config,
            eventEmitter: this.eventEmitter
        });
    }
}
